import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import "../../../styles/Calendar.scss";

interface Props {
  availableDates: string[];
  selectedDate?: string | null;
  isSingleTicket?: boolean;
  onSelect: (date: string) => void;
}

const Calendar: React.FC<Props> = ({
  availableDates,
  selectedDate,
  isSingleTicket = false,
  onSelect,
}) => {
  const getDateKey = (
    dateValue: string | Date
  ): string => {
    const date =
      dateValue instanceof Date
        ? dateValue
        : new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    const year = date.getFullYear();

    const month = String(
      date.getMonth() + 1
    ).padStart(2, "0");

    const day = String(
      date.getDate()
    ).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const validAvailableDates =
    useMemo(() => {
      if (
        !Array.isArray(availableDates)
      ) {
        return [];
      }

      return [...availableDates]
        .filter(
          (date) =>
            !Number.isNaN(
              new Date(date).getTime()
            )
        )
        .sort(
          (firstDate, secondDate) =>
            new Date(
              firstDate
            ).getTime() -
            new Date(
              secondDate
            ).getTime()
        );
    }, [availableDates]);

  const availableDateMap = useMemo(() => {
    const map = new Map<
      string,
      string[]
    >();

    validAvailableDates.forEach(
      (date) => {
        const dateKey =
          getDateKey(date);

        if (!dateKey) {
          return;
        }

        const existingDates =
          map.get(dateKey) || [];

        existingDates.push(date);
        map.set(
          dateKey,
          existingDates
        );
      }
    );

    return map;
  }, [validAvailableDates]);

  const availableDateKeys =
    useMemo(() => {
      return new Set(
        availableDateMap.keys()
      );
    }, [availableDateMap]);

  const getInitialMonth =
    (): Date => {
      const preferredDate =
        selectedDate ||
        validAvailableDates[0];

      if (preferredDate) {
        const parsedDate =
          new Date(preferredDate);

        if (
          !Number.isNaN(
            parsedDate.getTime()
          )
        ) {
          return new Date(
            parsedDate.getFullYear(),
            parsedDate.getMonth(),
            1
          );
        }
      }

      const today = new Date();

      return new Date(
        today.getFullYear(),
        today.getMonth(),
        1
      );
    };

  const [
    displayedMonth,
    setDisplayedMonth,
  ] = useState<Date>(getInitialMonth);

  useEffect(() => {
    const preferredDate =
      selectedDate ||
      validAvailableDates[0];

    if (!preferredDate) {
      return;
    }

    const parsedDate =
      new Date(preferredDate);

    if (
      Number.isNaN(
        parsedDate.getTime()
      )
    ) {
      return;
    }

    setDisplayedMonth(
      new Date(
        parsedDate.getFullYear(),
        parsedDate.getMonth(),
        1
      )
    );
  }, [
    selectedDate,
    validAvailableDates,
    isSingleTicket,
  ]);

  const selectedDateKey =
    selectedDate
      ? getDateKey(selectedDate)
      : "";

  const currentYear =
    displayedMonth.getFullYear();

  const currentMonth =
    displayedMonth.getMonth();

  const firstDayOfMonth = new Date(
    currentYear,
    currentMonth,
    1
  ).getDay();

  const totalDaysInMonth = new Date(
    currentYear,
    currentMonth + 1,
    0
  ).getDate();

  const calendarCells: Array<
    number | null
  > = [];

  for (
    let index = 0;
    index < firstDayOfMonth;
    index += 1
  ) {
    calendarCells.push(null);
  }

  for (
    let day = 1;
    day <= totalDaysInMonth;
    day += 1
  ) {
    calendarCells.push(day);
  }

  while (
    calendarCells.length % 7 !==
    0
  ) {
    calendarCells.push(null);
  }

  const goToPreviousMonth = () => {
    setDisplayedMonth(
      new Date(
        currentYear,
        currentMonth - 1,
        1
      )
    );
  };

  const goToNextMonth = () => {
    setDisplayedMonth(
      new Date(
        currentYear,
        currentMonth + 1,
        1
      )
    );
  };

  const handleDateSelect = (
    day: number
  ) => {
    if (isSingleTicket) {
      return;
    }

    const calendarDate = new Date(
      currentYear,
      currentMonth,
      day
    );

    const dateKey =
      getDateKey(calendarDate);

    const matchingDates =
      availableDateMap.get(dateKey);

    if (
      !matchingDates ||
      matchingDates.length === 0
    ) {
      return;
    }

    onSelect(matchingDates[0]);
  };

  const monthLabel =
    displayedMonth.toLocaleDateString(
      "en-US",
      {
        month: "long",
        year: "numeric",
      }
    );

  const weekDays = [
    "Sun",
    "Mon",
    "Tue",
    "Wed",
    "Thu",
    "Fri",
    "Sat",
  ];

  if (
    validAvailableDates.length === 0
  ) {
    return (
      <div className="calendar-section">
        <h3>Select Date</h3>

        <div className="calendar-empty">
          No available event dates.
        </div>
      </div>
    );
  }

  return (
    <div className="calendar-section">
      <h3>
        {isSingleTicket
          ? "Event Dates"
          : "Select Date"}
      </h3>

      <div className="event-calendar">
        <div className="calendar-header">
          <button
            type="button"
            className="calendar-navigation"
            onClick={
              goToPreviousMonth
            }
            aria-label="Previous month"
          >
            &#8249;
          </button>

          <h4>{monthLabel}</h4>

          <button
            type="button"
            className="calendar-navigation"
            onClick={goToNextMonth}
            aria-label="Next month"
          >
            &#8250;
          </button>
        </div>

        <div className="calendar-weekdays">
          {weekDays.map(
            (weekDay) => (
              <div
                key={weekDay}
                className="calendar-weekday"
              >
                {weekDay}
              </div>
            )
          )}
        </div>

        <div className="calendar-grid">
          {calendarCells.map(
            (day, index) => {
              if (day === null) {
                return (
                  <div
                    key={`empty-${index}`}
                    className="calendar-day empty"
                    aria-hidden="true"
                  />
                );
              }

              const date = new Date(
                currentYear,
                currentMonth,
                day
              );

              const dateKey =
                getDateKey(date);

              const isAvailable =
                availableDateMap.has(
                  dateKey
                );

              const isSelected =
                isSingleTicket
                  ? availableDateKeys.has(
                      dateKey
                    )
                  : selectedDateKey ===
                    dateKey;

              const isDateDisabled =
                !isAvailable ||
                isSingleTicket;

              return (
                <button
                  key={`${dateKey}-${String(
                    isSingleTicket
                  )}`}
                  type="button"
                  disabled={
                    isDateDisabled
                  }
                  className={[
                    "calendar-day",
                    isAvailable
                      ? "available"
                      : "unavailable",
                    isSelected
                      ? "selected"
                      : "",
                    isSingleTicket &&
                    isAvailable
                      ? "single-ticket-selected"
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() =>
                    handleDateSelect(
                      day
                    )
                  }
                  aria-label={`${date.toLocaleDateString(
                    "en-US",
                    {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    }
                  )}${
                    isSelected
                      ? ", selected"
                      : isAvailable
                        ? ", available"
                        : ", unavailable"
                  }`}
                  aria-pressed={
                    isSelected
                  }
                >
                  {day}
                </button>
              );
            }
          )}
        </div>

        <div className="calendar-legend">
          {!isSingleTicket && (
            <div className="legend-item">
              <span className="legend-box available" />
              Available
            </div>
          )}

          <div className="legend-item">
            <span className="legend-box unavailable" />
            Unavailable
          </div>

          <div className="legend-item">
            <span className="legend-box selected" />
            Selected
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calendar;